import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3 w-full", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 w-full",
        month: "space-y-4 w-full",
        caption: "flex justify-center pt-1 relative items-center mb-2",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse border border-border/50",
        head_row: "flex w-full",
        head_cell: "text-muted-foreground w-full font-semibold text-xs border-b border-border/50 py-2 bg-muted/30",
        row: "flex w-full",
        cell: "w-full h-12 text-center text-sm p-0 relative border-r border-b border-border/50 last:border-r-0 hover:bg-muted/50 transition-colors focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-full w-full p-0 font-normal aria-selected:opacity-100 rounded-none hover:bg-transparent"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary/20 border-2 border-primary text-foreground hover:bg-primary/30 hover:text-foreground focus:bg-primary/30 focus:text-foreground font-bold",
        day_today: "bg-accent/30 text-accent-foreground font-semibold",
        day_outside:
          "day-outside text-muted-foreground/40 opacity-50",
        day_disabled: "text-muted-foreground opacity-30",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
